import { Response } from 'datx-jsonapi';
import { FC, useMemo } from 'react';

import { DropdownSelect } from '@/components/ui/shared/DropdownSelect/DropdownSelect';
import { SearchCategories } from '@/enums';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useResourceRelationship, useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { queryThinEventSessionsByQueryInfinite } from '@/queries/thin-event-sessions';
import { queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { EventConfiguration } from '@/resources/EventConfiguration';
import { ThinEventSession } from '@/resources/ThinEventSession';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { StringParam } from '@/utils/url-query-string';

import { IDropdownItem } from '../EventDropdown';

function renderItem(eventSessionName: string, eventSessionId: string, eventId: string, scope: string): IDropdownItem {
  const eventSessionIdQuery = eventSessionId ? `?eventSessionId=${eventSessionId}` : '';

  const navigationRoute = scope === 'lectures' ? 'sessions' : 'posters';

  return {
    id: eventSessionId,
    value: eventSessionName,
    href: `/events/[eventId]/${navigationRoute}${eventSessionIdQuery}`,
    as: `/events/${eventId}/${navigationRoute}${eventSessionIdQuery}`,
  };
}

function prepareSessions(
  eventSessions: Array<ThinEventSession>,
  eventId: string,
  scope: string,
  defaultValueLabel: string
) {
  const defaultValue = renderItem(defaultValueLabel || `all sessions`, '', eventId, scope);
  const values = eventSessions?.map((session: ThinEventSession) =>
    renderItem(session.name, session.id, eventId, scope)
  );

  return [defaultValue, ...values];
}

interface IEventLectureDropdownProps {
  scope: 'lectures' | 'posters';
}

export const EventSessionsDropdown: FC<IEventLectureDropdownProps> = ({ scope }) => {
  const { eventId, eventSessionId, trackIds, change } = useQueryParams<{
    eventId: string;
    eventSessionId: string;
    trackIds: string;
  }>({
    eventId: StringParam,
    eventSessionId: StringParam,
    trackId: StringParam,
  });

  const { data: eventConfiguration } = useResourceRelationship<EventConfiguration>(
    queryThinEventConfigurationRelationship(eventId)
  );

  const filterScope = scope === 'lectures' ? 'with_lectures' : '';

  const { data: sessions, isValidating, error, size, setSize } = useResourcesInfinite(
    queryThinEventSessionsByQueryInfinite({
      filters: {
        event_id: eventId,
        scope: filterScope,
        listed: 'true',
        track_id: trackIds,
        poster_only: scope === SearchCategories.POSTERS ? 'true' : null,
      },
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { items, isReachingEnd, isLoadingMore: isLoadingSessions } = useMemo(
    () => extractSWRInfiniteData<ThinEventSession>(sessions as Array<Response<ThinEventSession>>, error, size),
    [sessions, error, size]
  );

  const onClickItem = (item: IDropdownItem) => {
    change({ eventSessionId: item.id, eventId, trackIds }, { shallow: true });
  };

  const options = prepareSessions(items, eventId, scope, eventConfiguration?.sessionsLabelName);
  const selectedOption = options.find((o) => o.id === eventSessionId);

  return (
    <DropdownSelect
      maxW="200px"
      options={options}
      selectedOption={selectedOption?.value ? selectedOption : options[0]}
      onClickOption={onClickItem}
      setSize={setSize}
      size={size}
      isLoading={isLoadingSessions}
      error={error}
      isReachingEnd={isReachingEnd}
    />
  );
};
